// v10/src/components/admin/AdminAircraft.js
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plane, Plus, Edit2, Trash2, ChevronDown, ChevronUp,
  Check, X, AlertCircle, Loader, List, Search, ToggleLeft, ToggleRight,
  Wrench, AlertTriangle, QrCode, Save, Upload, Fuel,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import QRModal from '../QRModal';
import { aircraftService, checklistService, preLandingService } from '../../services/apiService';
import api from '../../services/apiService';
import serviceComponentsService from '../../services/serviceComponentsService';
import { useToast } from '../../hooks/useToast';
import GeneralConfirmModal from '../common/ConfirmModal';
import AogBadge from '../aircraft/AogBadge';
import EquipmentTab from './aircraft/EquipmentTab';
import { ComponentCard, MarkServiceForm, HistoryModalBody } from './service/ServiceTab';

// ─── Delete Modal ─────────────────────────────────────────────────
const DeleteModal = ({ aircraft, onConfirm, onClose, darkMode }) => {
  const { t } = useTranslation('admin');
  const [loading, setLoading] = useState(false);
  const handle = async (permanent) => {
    setLoading(true);
    try { await onConfirm(aircraft.id, permanent); onClose(); }
    catch { setLoading(false); }
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}>
      <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
        className={`w-full max-w-sm rounded-3xl shadow-2xl p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
        onClick={e => e.stopPropagation()}>
        <div className="flex flex-col items-center text-center gap-3 mb-6">
          <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center">
            <AlertTriangle className="w-7 h-7 text-red-500" />
          </div>
          <div>
            <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{t('delete_aircraft_title')}</h3>
            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <strong>{aircraft.manufacturer} {aircraft.model_name}</strong>
              {aircraft.registration ? ` (${aircraft.registration})` : ''}
            </p>
          </div>
        </div>
        <div className="space-y-3 mb-4">
          <button onClick={() => handle(false)} disabled={loading}
            className={`w-full p-4 rounded-2xl border-2 text-left transition-all ${darkMode ? 'border-amber-600/40 bg-amber-900/20 hover:bg-amber-900/40' : 'border-amber-200 bg-amber-50 hover:bg-amber-100'}`}>
            <p className={`font-bold text-sm ${darkMode ? 'text-amber-400' : 'text-amber-700'}`}>{t('deactivate_option')}</p>
            <p className={`text-xs mt-0.5 ${darkMode ? 'text-amber-500/70' : 'text-amber-600/80'}`}>{t('deactivate_desc')}</p>
          </button>
          <button onClick={() => handle(true)} disabled={loading}
            className={`w-full p-4 rounded-2xl border-2 text-left transition-all ${darkMode ? 'border-red-600/40 bg-red-900/20 hover:bg-red-900/40' : 'border-red-200 bg-red-50 hover:bg-red-100'}`}>
            <p className={`font-bold text-sm ${darkMode ? 'text-red-400' : 'text-red-700'}`}>{t('delete_permanent_option')}</p>
            <p className={`text-xs mt-0.5 ${darkMode ? 'text-red-500/70' : 'text-red-600/80'}`}>{t('delete_permanent_desc')}</p>
          </button>
        </div>
        <button onClick={onClose} disabled={loading}
          className={`w-full py-3 rounded-2xl font-semibold text-sm transition-all ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          {t('cancel', { ns: 'common' })}
        </button>
      </motion.div>
    </motion.div>
  );
};

// ─── Modal wrapper para el widget de componentes ─────────────────
// Mimetiza el patrón de ServiceTab (backdrop click + ESC para cerrar).
const ServiceComponentsModal = ({ open, onClose, title, children, darkMode }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 16,
      }}>
      <motion.div
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.96, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: darkMode ? '#1e293b' : 'white',
          color: darkMode ? 'white' : '#0f172a',
          borderRadius: 18, width: '100%', maxWidth: 520, padding: 24,
          boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
        }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>{title}</h3>
          <button onClick={onClose} type="button"
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'inherit', padding: 4 }}>
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>
        {children}
      </motion.div>
    </motion.div>
  );
};

// ── Audio Offline Section ─────────────────────────────────────────
// Polling de /api/tts/pregenerate-status/:id para mostrar barra X/N real.
// Detecta jobs disparados por la auto-regeneración (al editar el
// checklist) o por el botón manual.
const AudioOfflineSection = ({ aircraftId, darkMode }) => {
  const [status, setStatus]     = React.useState(null); // { count, updatedAt }
  const [job, setJob]           = React.useState(null); // { status, done, total, generated, errors, mode }
  const [error, setError]       = React.useState('');
  const [done, setDone]         = React.useState(false);
  const pollRef                 = React.useRef(null);

  const loadStatus = async () => {
    try {
      const res = await api.get(`/api/tts/cache/${aircraftId}`);
      const manifest = res.data?.manifest || {};
      setStatus({ count: Object.keys(manifest).length, updatedAt: res.data?.updatedAt });
    } catch (_) { setStatus({ count: 0, updatedAt: null }); }
  };

  const fetchJobStatus = async () => {
    try {
      const res = await api.get(`/api/tts/pregenerate-status/${aircraftId}`);
      return res.data;
    } catch (_) { return null; }
  };

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const startPolling = () => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      const data = await fetchJobStatus();
      if (!data) return;
      setJob(data);
      if (data.status === 'completed' || data.status === 'error') {
        stopPolling();
        setDone(data.status === 'completed');
        if (data.errors > 0 && data.generated === 0) {
          setError(`No se pudo generar audios. ${data.errorList?.[0]?.error || ''}`);
        } else if (data.errors > 0) {
          setError(`${data.errors} ítems fallaron. ${data.generated}/${data.total} generados.`);
        } else {
          setError('');
        }
        await loadStatus();
      }
    }, 1500);
  };

  // Carga inicial: estado del cache + polling si hay job activo
  React.useEffect(() => {
    if (!aircraftId) return;
    loadStatus();
    fetchJobStatus().then(data => {
      if (!data) return;
      setJob(data);
      if (data.status === 'running' || data.status === 'pending') {
        startPolling();
      }
    });
    return stopPolling;
  }, [aircraftId]);

  const handleGenerate = async () => {
    setError(''); setDone(false);
    try {
      // ?force=true → regen completa (botón manual mantiene su semántica)
      const res = await api.post(`/api/tts/pregenerate/${aircraftId}?force=true`);
      setJob({
        status:    res.data.status,
        total:     res.data.total ?? 0,
        done:      res.data.done ?? 0,
        generated: res.data.generated ?? 0,
        errors:    res.data.errors ?? 0,
        mode:      res.data.mode,
      });
      if (res.data.status === 'running') startPolling();
      else { setDone(true); await loadStatus(); }
    } catch (err) {
      setError(err.response?.data?.message || 'Error al generar audios.');
    }
  };

  const running = job?.status === 'running' || job?.status === 'pending';
  const total   = job?.total ?? 0;
  const doneN   = job?.done ?? 0;
  const pct     = total > 0 ? Math.round((doneN / total) * 100) : 0;
  const sub     = darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8';

  return (
    <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ padding:'12px 14px', borderRadius:12, background:darkMode?'rgba(139,92,246,0.1)':'rgba(237,233,254,0.6)', border:darkMode?'1px solid rgba(139,92,246,0.2)':'1px solid rgba(139,92,246,0.15)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:18 }}>🎙️</span>
          <div>
            <p style={{ margin:0, fontSize:13, fontWeight:700, color:darkMode?'rgba(196,181,253,0.9)':'#7c3aed' }}>
              {status===null?'Cargando...':status.count===0?'Sin audios generados':`${status.count} audios en cache`}
            </p>
            {status?.updatedAt && <p style={{ margin:0, fontSize:11, color:sub }}>Generado: {new Date(status.updatedAt).toLocaleDateString('es-AR',{day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'})}</p>}
          </div>
        </div>
        {status?.count>0 && !running && <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:99, background:'rgba(139,92,246,0.15)', color:darkMode?'#c4b5fd':'#7c3aed' }}>✅ Offline listo</span>}
      </div>
      <p style={{ margin:0, fontSize:12, color:sub, lineHeight:1.5 }}>Genera los audios de todos los ítems del checklist via Valentina (ElevenLabs) y los sube a Cloudinary. Los pilotos los descargan automáticamente al seleccionar la aeronave — funcionan sin internet.</p>

      {running && (
        <div style={{ padding:'12px 14px', borderRadius:12, background:darkMode?'rgba(255,255,255,0.04)':'#f8fafc', border:darkMode?'1px solid rgba(255,255,255,0.08)':'1px solid #e2e8f0' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ width:14, height:14, borderRadius:'50%', border:'2px solid #8b5cf6', borderTopColor:'transparent', animation:'spin 0.8s linear infinite' }} />
              <span style={{ fontSize:12, fontWeight:600, color:darkMode?'rgba(255,255,255,0.7)':'#374151' }}>
                {job?.status === 'pending' ? 'Esperando cambios…' : `Generando voces ${doneN}/${total}…`}
              </span>
            </div>
            <span style={{ fontSize:11, fontWeight:700, color:'#8b5cf6' }}>{pct}%</span>
          </div>
          <div style={{ height:6, borderRadius:3, background:darkMode?'rgba(55,65,81,1)':'rgba(229,231,235,1)', overflow:'hidden' }}>
            <div style={{ width:`${pct}%`, height:'100%', background:'linear-gradient(90deg,#8b5cf6,#7c3aed)', transition:'width 0.4s ease' }} />
          </div>
          {job?.mode === 'incremental' && <p style={{ margin:'6px 0 0', fontSize:11, color:sub }}>Actualización automática post-cambio (incremental).</p>}
        </div>
      )}

      {done && !running && !error && <p style={{ margin:0, fontSize:12, fontWeight:700, color:'#16a34a', background:'rgba(22,163,74,0.08)', padding:'8px 12px', borderRadius:10, border:'1px solid rgba(22,163,74,0.2)' }}>✅ Audios generados correctamente.</p>}
      {error && <p style={{ margin:0, fontSize:12, color:'#ef4444', background:'rgba(239,68,68,0.08)', padding:'8px 12px', borderRadius:10, border:'1px solid rgba(239,68,68,0.2)' }}>⚠️ {error}</p>}
      <button onClick={handleGenerate} disabled={running}
        style={{ padding:'12px 16px', borderRadius:14, border:'none', background:running?'#64748b':'linear-gradient(135deg,#8b5cf6,#7c3aed)', color:'white', fontWeight:700, fontSize:13, fontFamily:'inherit', cursor:running?'not-allowed':'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
        {running
          ? <><span style={{ width:14,height:14,borderRadius:'50%',border:'2px solid rgba(255,255,255,0.4)',borderTopColor:'white',animation:'spin 0.8s linear infinite',display:'inline-block' }} /> Generando audios...</>
          : <>{status?.count>0?'🔄 Regenerar audios offline':'🎙️ Generar audios offline'}</>}
      </button>
      <p style={{ margin:0, fontSize:11, color:sub, textAlign:'center' }}>Este proceso puede demorar según la cantidad de ítems. No cerrés esta pantalla.</p>
    </div>
  );
};

// ─── Service Components Widget (sistema nuevo) ────────────────────
// Reemplaza al antiguo HobbsWidget: lista los componentes de service del
// sistema nuevo (serviceComponentsService) para esta aeronave. Permite
// registrar "Service hecho" por componente y abre historial; alta/edición/
// borrado se manejan en el panel global de Mantenimiento (CTA al pie).
const MANAGE_SERVICE_ROLES = new Set(['admin', 'instructor', 'superadmin']);
const DELETE_SERVICE_ROLES = new Set(['admin', 'superadmin']);

const AircraftServiceComponentsWidget = ({ aircraftId, aircraft, userRole, darkMode, onNavigateToMaintenance }) => {
  const { t } = useTranslation('admin');
  const toast = useToast();
  const canManage = MANAGE_SERVICE_ROLES.has(userRole);
  const canDelete = DELETE_SERVICE_ROLES.has(userRole);
  const [components, setComponents]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [marking, setMarking]         = useState(null);
  const [history, setHistory]         = useState(null);
  const [deleting, setDeleting]       = useState(null);
  const [deleteBusy, setDeleteBusy]   = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [restoring, setRestoring]     = useState(null);
  const [restoreBusy, setRestoreBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = showInactive ? { include_inactive: 'true' } : {};
      const r = await serviceComponentsService.listByAircraft(aircraftId, params);
      setComponents(r?.data?.data || []);
    } catch (_) { setComponents([]); }
    finally { setLoading(false); }
  }, [aircraftId, showInactive]);

  useEffect(() => { load(); }, [load]);

  const handleMark = async (data) => {
    try {
      await serviceComponentsService.markServiceDone(aircraftId, marking.id, data);
      toast?.success?.(t('service_mark_ok'));
      setMarking(null);
      await load();
    } catch (e) {
      toast?.error?.(e?.response?.data?.message || t('service_mark_err'));
    }
  };

  const handleDelete = async () => {
    if (!deleting || deleteBusy) return;
    setDeleteBusy(true);
    try {
      await serviceComponentsService.remove(aircraftId, deleting.id);
      toast?.success?.(t('service_delete_ok'));
      setDeleting(null);
      await load();
    } catch (e) {
      toast?.error?.(e?.response?.data?.message || t('service_delete_err'));
    } finally {
      setDeleteBusy(false);
    }
  };

  const handleRestore = async () => {
    if (!restoring || restoreBusy) return;
    setRestoreBusy(true);
    try {
      await serviceComponentsService.restore(aircraftId, restoring.id);
      toast?.success?.(t('service_restore_ok'));
      setRestoring(null);
      await load();
    } catch (e) {
      toast?.error?.(e?.response?.data?.message || t('service_restore_err'));
    } finally {
      setRestoreBusy(false);
    }
  };

  const activeComps   = components.filter((c) => c.is_active !== false);
  const inactiveComps = components.filter((c) => c.is_active === false);

  const ctaLabel = '🔧 Ver en Mantenimiento → Services recurrentes';
  const ctaBtn = (
    <button onClick={onNavigateToMaintenance}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 14px',
        borderRadius: 12, border: 'none', cursor: 'pointer',
        fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
        background: darkMode ? 'rgba(99,102,241,0.15)' : '#eef2ff',
        color: '#6366f1',
      }}>
      {ctaLabel}
    </button>
  );

  return (
    <div className="mt-4">
      {canDelete && (
        <div style={{
          display: 'flex', justifyContent: 'flex-end', marginBottom: 10,
        }}>
          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '6px 10px', borderRadius: 10, cursor: 'pointer',
            background: darkMode ? 'rgba(255,255,255,0.04)' : '#f1f5f9',
            color: darkMode ? 'rgba(255,255,255,0.85)' : '#334155',
            fontSize: 12, fontWeight: 600, userSelect: 'none',
          }}>
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            {t('service_show_inactive')}
          </label>
        </div>
      )}
      {loading ? (
        <div className="flex justify-center py-6"><Loader className="w-5 h-5 animate-spin text-amber-500" /></div>
      ) : components.length === 0 ? (
        <div style={{
          padding: 32, borderRadius: 16, textAlign: 'center',
          border: darkMode ? '1px dashed rgba(255,255,255,0.12)' : '1px dashed #cbd5e1',
          background: darkMode ? 'rgba(255,255,255,0.02)' : '#f8fafc',
        }}>
          <Wrench style={{ width: 32, height: 32, color: '#6366f1', opacity: 0.6, margin: '0 auto' }} />
          <p style={{ margin: '12px 0 0', fontSize: 14, fontWeight: 700 }}>
            {t('service_empty_title')}
          </p>
          <p style={{ margin: '4px 0 12px', fontSize: 12, opacity: 0.7 }}>
            {t('service_empty_desc')}
          </p>
          {ctaBtn}
        </div>
      ) : (
        <>
          <div style={{
            display: 'grid', gap: 12,
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          }}>
            {activeComps.map((c) => (
              <ComponentCard
                key={c.id} comp={c} locale="es-AR" darkMode={darkMode}
                canManage={canManage} canDelete={canDelete}
                onMarkService={() => setMarking(c)}
                onEdit={onNavigateToMaintenance}
                onDelete={() => setDeleting(c)}
                onHistory={() => setHistory(c)}
              />
            ))}
            {inactiveComps.map((c) => (
              <div key={c.id} style={{
                padding: 16, borderRadius: 16,
                background: darkMode ? 'rgba(255,255,255,0.02)' : '#f8fafc',
                border: darkMode ? '1px dashed rgba(255,255,255,0.12)' : '1px dashed #cbd5e1',
                color: darkMode ? 'rgba(255,255,255,0.72)' : '#475569',
                display: 'flex', flexDirection: 'column', gap: 10, opacity: 0.85,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 15, fontWeight: 800 }}>{c.name}</p>
                    <p style={{ margin: '3px 0 0', fontSize: 12, opacity: 0.75 }}>
                      {t('service_card_interval', { hours: Number(c.interval_hours).toFixed(1) })}
                    </p>
                  </div>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '4px 10px', borderRadius: 999,
                    background: darkMode ? 'rgba(148,163,184,0.18)' : '#e2e8f0',
                    color: darkMode ? 'rgba(226,232,240,0.9)' : '#475569',
                    fontSize: 11, fontWeight: 800,
                  }}>
                    {t('service_inactive_badge')}
                  </span>
                </div>
                {canDelete && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button type="button" onClick={() => setRestoring(c)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '8px 12px', borderRadius: 10, border: 'none',
                        cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 700,
                        background: '#16a34a', color: 'white',
                      }}>
                      <Check style={{ width: 13, height: 13 }} />
                      {t('service_card_btn_restore')}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div style={{
            marginTop: 14, paddingTop: 14,
            borderTop: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
            // Alineado a la izquierda para no solaparse con la campanita de
            // notificaciones (fija en la esquina inferior derecha del layout).
            display: 'flex', justifyContent: 'flex-start',
          }}>
            {ctaBtn}
          </div>
        </>
      )}

      <AnimatePresence>
        {marking && (
          <ServiceComponentsModal open onClose={() => setMarking(null)}
            title={t('service_modal_mark_title')} darkMode={darkMode}>
            <MarkServiceForm component={marking} aircraft={aircraft}
              onSubmit={handleMark} onCancel={() => setMarking(null)} darkMode={darkMode} />
          </ServiceComponentsModal>
        )}
        {history && (
          <ServiceComponentsModal open onClose={() => setHistory(null)}
            title={t('service_modal_history_title', { name: history?.name || '' })} darkMode={darkMode}>
            <HistoryModalBody component={history} aircraft={aircraft} darkMode={darkMode} />
          </ServiceComponentsModal>
        )}
        {deleting && (
          <ServiceComponentsModal open onClose={() => !deleteBusy && setDeleting(null)}
            title={t('service_modal_delete_title')} darkMode={darkMode}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <p style={{ margin: 0, fontSize: 13 }}>
                {t('service_modal_delete_confirm', { name: deleting.name })}
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" onClick={() => setDeleting(null)} disabled={deleteBusy}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '9px 14px', borderRadius: 10,
                    border: darkMode ? '1px solid rgba(255,255,255,0.22)' : '1px solid #94a3b8',
                    cursor: deleteBusy ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                    background: darkMode ? 'rgba(255,255,255,0.08)' : 'white',
                    color: darkMode ? 'rgba(255,255,255,0.92)' : '#1e293b',
                  }}>
                  {t('service_action_cancel')}
                </button>
                <button type="button" onClick={handleDelete} disabled={deleteBusy}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '9px 14px', borderRadius: 10, border: 'none',
                    cursor: deleteBusy ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                    background: '#dc2626', color: 'white',
                  }}>
                  {deleteBusy
                    ? <Loader style={{ width: 14, height: 14 }} />
                    : <Trash2 style={{ width: 14, height: 14 }} />}
                  {t('service_action_delete')}
                </button>
              </div>
            </div>
          </ServiceComponentsModal>
        )}
        {restoring && (
          <ServiceComponentsModal open onClose={() => !restoreBusy && setRestoring(null)}
            title={t('service_modal_restore_title')} darkMode={darkMode}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <p style={{ margin: 0, fontSize: 13 }}>
                {t('service_modal_restore_confirm', { name: restoring.name })}
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" onClick={() => setRestoring(null)} disabled={restoreBusy}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '9px 14px', borderRadius: 10,
                    border: darkMode ? '1px solid rgba(255,255,255,0.22)' : '1px solid #94a3b8',
                    cursor: restoreBusy ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                    background: darkMode ? 'rgba(255,255,255,0.08)' : 'white',
                    color: darkMode ? 'rgba(255,255,255,0.92)' : '#1e293b',
                  }}>
                  {t('service_action_cancel')}
                </button>
                <button type="button" onClick={handleRestore} disabled={restoreBusy}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '9px 14px', borderRadius: 10, border: 'none',
                    cursor: restoreBusy ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                    background: '#16a34a', color: 'white',
                  }}>
                  {restoreBusy
                    ? <Loader style={{ width: 14, height: 14 }} />
                    : <Check style={{ width: 14, height: 14 }} />}
                  {t('service_action_restore')}
                </button>
              </div>
            </div>
          </ServiceComponentsModal>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Pre-Landing Section ──────────────────────────────────────────
const STEP_TYPE_OPTIONS = [
  { value: 'manual', label: '🖐 Manual — el piloto confirma' },
  { value: 'auto',   label: '⏱ Auto — avanza solo con delay' },
  { value: 'gate',   label: '🏁 Gate — espera confirmación de recta final' },
];
const ICON_OPTIONS  = ['🛬','🎛️','🌡️','💡','🏁','⚙️','🔧','⛽','🛞','📻','🚨','✅','👀','✈️','🔄','📋'];
const COLOR_OPTIONS = ['#2563eb','#d97706','#7c3aed','#0891b2','#16a34a','#ef4444','#f59e0b','#ec4899','#64748b'];

const PreLandingSection = ({ aircraftId, darkMode }) => {
  const toast = useToast();
  const [steps, setSteps]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  const [saved, setSaved]     = useState(false);

  const inputClass = `px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`;

  const load = async () => {
    setLoading(true);
    try {
      const res = await preLandingService.getSteps(aircraftId);
      setSteps(res.data?.data || []); setIsDefault(res.data?.is_default || false);
    } catch { } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [aircraftId]);

  const handleSave = async () => {
    setSaving(true);
    try { await preLandingService.saveSteps(aircraftId, steps); setSaved(true); setIsDefault(false); setTimeout(() => setSaved(false), 2500); }
    catch { toast.error('Error al guardar'); } finally { setSaving(false); }
  };

  const handleSeedDefaults = async () => {
    setSaving(true);
    try { await preLandingService.seedDefaults(aircraftId); await load(); }
    catch { toast.error('Error al cargar defaults'); } finally { setSaving(false); }
  };

  const updateStep = (idx, field, value) => setSteps(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  const addStep = () => setSteps(prev => [...prev, { step_id:`step_${Date.now()}`, icon:'🛬', title:'', description:'', voice_text:'', step_type:'auto', delay_seconds:3, color:'#0891b2' }]);
  const removeStep = (idx) => setSteps(prev => prev.filter((_, i) => i !== idx));
  const moveStep = (idx, dir) => {
    const arr = [...steps]; const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]]; setSteps(arr);
  };

  if (loading) return <div className="flex justify-center py-6"><Loader className="w-5 h-5 animate-spin text-cyan-500" /></div>;

  return (
    <div className="mt-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <p className={`text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>🛬 Pasos de Pre-aterrizaje</p>
          {isDefault && <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Usando pasos default — personalizalos para esta aeronave</p>}
        </div>
        <div className="flex gap-2">
          {steps.length === 0 && (
            <button onClick={handleSeedDefaults} disabled={saving}
              className={`text-xs px-3 py-1.5 rounded-lg font-bold transition-all ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              Cargar defaults
            </button>
          )}
          <button onClick={handleSave} disabled={saving || steps.length === 0}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white font-bold transition-all disabled:opacity-40">
            {saving ? <Loader className="w-3 h-3 animate-spin" /> : saved ? <Check className="w-3 h-3" /> : <Save className="w-3 h-3" />}
            {saved ? '¡Guardado!' : 'Guardar'}
          </button>
        </div>
      </div>
      {steps.length === 0 ? (
        <div className={`p-6 rounded-2xl border-2 border-dashed text-center ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
          <p className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No hay pasos configurados para esta aeronave</p>
          <button onClick={handleSeedDefaults} disabled={saving} className="text-xs px-4 py-2 rounded-xl bg-cyan-500 hover:bg-cyan-600 text-white font-bold transition-all">Cargar pasos default</button>
        </div>
      ) : (
        <div className="space-y-2">
          {steps.map((step, idx) => (
            <div key={idx} className={`p-3 rounded-xl border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className="flex flex-col gap-0.5">
                  <button onClick={() => moveStep(idx,-1)} disabled={idx===0} className={`text-xs px-1 rounded ${darkMode?'hover:bg-gray-600 text-gray-400':'hover:bg-gray-200 text-gray-500'} disabled:opacity-20`}>▲</button>
                  <button onClick={() => moveStep(idx,1)} disabled={idx===steps.length-1} className={`text-xs px-1 rounded ${darkMode?'hover:bg-gray-600 text-gray-400':'hover:bg-gray-200 text-gray-500'} disabled:opacity-20`}>▼</button>
                </div>
                <span className={`text-xs font-black w-5 text-center ${darkMode?'text-gray-400':'text-gray-400'}`}>{idx+1}</span>
                <select value={step.icon} onChange={e => updateStep(idx,'icon',e.target.value)} className={`${inputClass} w-16 text-center`} style={{ fontSize:16 }}>
                  {ICON_OPTIONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                </select>
                <div className="flex gap-1 flex-wrap" style={{ maxWidth:80 }}>
                  {COLOR_OPTIONS.map(c => (
                    <button key={c} onClick={() => updateStep(idx,'color',c)} style={{ width:16, height:16, borderRadius:4, background:c, border:step.color===c?'2px solid white':'2px solid transparent', outline:step.color===c?`2px solid ${c}`:'none' }} />
                  ))}
                </div>
                <input value={step.title} onChange={e => updateStep(idx,'title',e.target.value)} placeholder="Título del paso *" className={`${inputClass} flex-1`} />
                <button onClick={() => removeStep(idx)} className="p-1.5 rounded-lg hover:bg-red-100 text-red-400 flex-shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <input value={step.description||''} onChange={e => updateStep(idx,'description',e.target.value)} placeholder="Descripción (visible al piloto)" className={`${inputClass} w-full`} />
                <input value={step.voice_text||''} onChange={e => updateStep(idx,'voice_text',e.target.value)} placeholder="Texto de voz (Valentina) *" className={`${inputClass} w-full`} />
              </div>
              <div className="flex gap-2 items-center">
                <select value={step.step_type} onChange={e => updateStep(idx,'step_type',e.target.value)} className={`${inputClass} flex-1`}>
                  {STEP_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {step.step_type === 'auto' && (
                  <div className="flex items-center gap-1.5">
                    <input type="number" min={0} max={60} value={step.delay_seconds} onChange={e => updateStep(idx,'delay_seconds',parseInt(e.target.value)||0)} className={`${inputClass} w-16 text-center`} />
                    <span className={`text-xs font-medium ${darkMode?'text-gray-400':'text-gray-500'}`}>seg</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <button onClick={addStep} className={`w-full py-2.5 rounded-2xl border-2 border-dashed flex items-center justify-center gap-2 text-sm font-medium transition-all ${darkMode?'border-gray-600 text-gray-400 hover:bg-gray-700':'border-gray-300 text-gray-500 hover:bg-gray-50'}`}>
        <Plus className="w-4 h-4" /> Agregar paso
      </button>
    </div>
  );
};

// ─── Checklist Section ────────────────────────────────────────────
// Fases fijas (Etapa 2 — editor multi-fase). Dropdown cerrado, mismo
// patrón que item_type — NO texto libre como section_name.
// FASES: hoy disponible para todos los aeroclubes (sin gate). Si se quiere
// hacer premium, cablear acá un check de orgFeatures.custom_checklists
// (hoy ese flag no se lee en ningún lado del código).
const PHASE_OPTIONS = [
  { value: 'prevuelo',          label: '🛫 Prevuelo' },
  { value: 'puesta_en_marcha',  label: '🔑 Puesta en marcha' },
  { value: 'prueba_motor',      label: '⚙️ Prueba de motor' },
];
const phaseLabel = (phase) => PHASE_OPTIONS.find(p => p.value === phase)?.label || PHASE_OPTIONS[0].label;

const ChecklistSection = ({ aircraftId, darkMode }) => {
  const { t } = useTranslation('admin');
  const toast = useToast();
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [adding, setAdding]       = useState(false);
  const [newItem, setNewItem]     = useState({ section_name:'', item_text:'', reference_image:'', section_order:0, item_order:0, item_type:'check', suggestion_text:'', has_suggestion:false, fuel_side:'', phase:'prevuelo' });
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText]   = useState('');
  const [editImage, setEditImage] = useState('');
  const [editPhase, setEditPhase] = useState('prevuelo');
  const [editSuggestion, setEditSuggestion]         = useState(false);
  const [editItemType, setEditItemType]             = useState('check');
  const [editFuelSide, setEditFuelSide]             = useState('');
  const [editSuggestionText, setEditSuggestionText] = useState('');
  const [sections, setSections]   = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deleteTargetId, setDeleteTargetId]   = useState(null);
  const [deletingItem, setDeletingItem]       = useState(false);

  const handleImageUpload = async (file, setImageFn, sectionName, itemName) => {
    if (!file) return;
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        const res = await api.post('/images/reference/upload', { image:base64, mediaType:file.type||'image/jpeg', aircraftId, sectionName:sectionName||'general', itemName:itemName||undefined });
        if (res.data?.success) setImageFn(res.data.data.url);
        setUploading(false);
      };
      reader.onerror = () => setUploading(false);
      reader.readAsDataURL(file);
    } catch { setUploading(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await checklistService.getByAircraft(aircraftId);
      const checklist = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
      setItems(checklist); setSections(checklist.map(s => s.section));
    } catch { } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [aircraftId]);

  const handleAddItem = async () => {
    if (!newItem.section_name || !newItem.item_text) return;
    const itemType = newItem.item_type || 'check';
    if (itemType === 'fuel' && !newItem.fuel_side) {
      toast.error('Elegí el lado del tanque (izquierdo, derecho o ambos)');
      return;
    }
    try {
      await checklistService.createItem({
        ...newItem,
        aircraft_model_id:aircraftId,
        item_type:itemType,
        has_suggestion:!!newItem.suggestion_text,
        suggestion_text:newItem.suggestion_text||null,
        fuel_side: itemType === 'fuel' ? newItem.fuel_side : null,
      });
      setNewItem({ section_name:'', item_text:'', reference_image:'', section_order:0, item_order:0, item_type:'check', suggestion_text:'', has_suggestion:false, fuel_side:'', phase:'prevuelo' });
      setAdding(false); await load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error al agregar item'); }
  };

  const handleDeleteClick = (id) => setDeleteTargetId(id);
  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    setDeletingItem(true);
    try { await checklistService.deleteItem(deleteTargetId, true); await load(); }
    catch { toast.error('Error al eliminar'); }
    finally { setDeletingItem(false); setDeleteTargetId(null); }
  };

  const handleEdit = async (id) => {
    if (editItemType === 'fuel' && !editFuelSide) {
      toast.error('Elegí el lado del tanque (izquierdo, derecho o ambos)');
      return;
    }
    try {
      await checklistService.updateItem(id, {
        item_text:editText,
        reference_image:editImage||null,
        has_suggestion:!!editSuggestionText,
        suggestion_text:editSuggestionText||null,
        item_type:editItemType,
        fuel_side: editItemType === 'fuel' ? editFuelSide : null,
        phase:editPhase,
      });
      setEditingId(null); await load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error al editar'); }
  };

  const inputClass = `px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`;

  if (loading) return <div className="flex justify-center py-4"><Loader className="w-5 h-5 animate-spin text-blue-500" /></div>;

  return (
    <div className="mt-4 space-y-4">
      {items.map((section, si) => (
        <div key={si}>
          <p className={`text-xs font-bold uppercase tracking-wider mb-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>{section.section}</p>
          <div className="space-y-1.5">
            {section.items.map((item) => (
              <div key={item.id} className={`flex items-center gap-2 p-2.5 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                {editingId === item.id ? (
                  <div className="flex-1 flex flex-col gap-1.5">
                    <input value={editText} onChange={e => setEditText(e.target.value)} className={`${inputClass} w-full`} autoFocus placeholder="Texto del item" />
			<div>
                          <label className={`block text-xs font-medium mb-1 ${darkMode?'text-gray-400':'text-gray-600'}`}>Tipo de ítem</label>
                          <select value={editItemType} onChange={e=>{const v=e.target.value;setEditItemType(v);if(v!=='fuel')setEditFuelSide('');}} className={`${inputClass} w-full`}>
                            <option value="check">✅ Ítem normal (check)</option>
                            <option value="hobbs">🕐 Horómetro</option>
                            <option value="oil">🛢 Aceite</option>
                            <option value="fuel">⛽ Combustible</option>
                          </select>
                        </div>
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${darkMode?'text-gray-400':'text-gray-600'}`}>Fase</label>
                      <select value={editPhase} onChange={e=>setEditPhase(e.target.value)} className={`${inputClass} w-full`}>
                        {PHASE_OPTIONS.map(p=><option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                    </div>
                    {editItemType==='fuel' && (
                      <div>
                        <label className={`block text-xs font-medium mb-1 ${darkMode?'text-gray-400':'text-gray-600'}`}>Lado del tanque *</label>
                        <select value={editFuelSide} onChange={e=>setEditFuelSide(e.target.value)} className={`${inputClass} w-full`}>
                          <option value="">— Seleccioná un lado —</option>
                          <option value="left">⬅ Izquierdo</option>
                          <option value="right">➡ Derecho</option>
                          <option value="both">⬅➡ Ambos (1 ítem, 2 inputs)</option>
                        </select>
                      </div>
                    )}
                    <div className="flex gap-1.5 items-center">
                      <label className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-all ${uploading?'opacity-50 pointer-events-none':''} ${darkMode?'bg-gray-600 text-white hover:bg-gray-500':'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                        {uploading?<Loader className="w-3.5 h-3.5 animate-spin"/>:<Upload className="w-3.5 h-3.5"/>}
                        {uploading?'Subiendo...':'Subir imagen'}
                        <input type="file" accept="image/*" className="hidden" onChange={e => { const f=e.target.files[0]; if(f) handleImageUpload(f,setEditImage,section.section,editText); e.target.value=''; }} />
                      </label>
                      {editImage && (<><img src={editImage} alt="preview" style={{ width:'32px',height:'32px',borderRadius:'6px',objectFit:'cover',flexShrink:0 }} onError={e=>e.target.style.display='none'} /><button onClick={()=>setEditImage('')} className="text-red-400 hover:text-red-500"><X className="w-3.5 h-3.5"/></button></>)}
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:8, padding:'4px 0' }}>
                      <input type="checkbox" id={`sug-edit-${item.id}`} checked={editSuggestion} onChange={e=>{setEditSuggestion(e.target.checked);if(!e.target.checked)setEditSuggestionText('');}} style={{ width:14,height:14,cursor:'pointer' }} />
                      <label htmlFor={`sug-edit-${item.id}`} style={{ fontSize:12,fontWeight:600,cursor:'pointer',color:darkMode?'rgba(255,255,255,0.7)':'#374151' }}>💡 Agregar sugerencia para el piloto</label>
                    </div>
                    {editSuggestion && <textarea value={editSuggestionText} onChange={e=>setEditSuggestionText(e.target.value)} className={`${inputClass} w-full`} rows={2} placeholder="Ej: Este avión consume 30L/hora..." />}
                    <div className="flex gap-1.5">
                      <button onClick={()=>handleEdit(item.id)} className="flex-1 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 text-xs font-semibold flex items-center justify-center gap-1"><Check className="w-3.5 h-3.5"/>Guardar</button>
                      <button onClick={()=>setEditingId(null)} className="px-3 py-1.5 bg-gray-200 rounded-lg hover:bg-gray-300 text-xs font-semibold">Cancelar</button>
                    </div>
                  </div>
                ) : (
                  <>
                    {item.reference_image && <img src={item.reference_image} alt={item.text} style={{ width:'36px',height:'36px',borderRadius:'8px',objectFit:'cover',flexShrink:0 }} onError={e=>e.target.style.display='none'} />}
                    <div style={{ flex:1, minWidth:0 }}>
                      <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{item.text}</span>
                      {item.suggestion_text && <p style={{ margin:'2px 0 0',fontSize:11,color:'#f59e0b',fontWeight:600 }}>💡 {item.suggestion_text.length>60?item.suggestion_text.slice(0,60)+'...':item.suggestion_text}</p>}
                    </div>
                    {item.item_type==='hobbs' && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold border border-amber-200 flex-shrink-0">🕐 Horómetro</span>}
                    {item.item_type==='oil' && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold border border-blue-200 flex-shrink-0">🛢 Aceite</span>}
                    {item.item_type==='fuel' && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold border border-emerald-200 flex-shrink-0">⛽ {item.fuel_side==='left'?'Comb. izq.':item.fuel_side==='right'?'Comb. der.':'Combustible'}</span>}
                    {item.phase && item.phase!=='prevuelo' && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-bold border border-purple-200 flex-shrink-0">{phaseLabel(item.phase)}</span>}
                    <button onClick={()=>{setEditingId(item.id);setEditText(item.text);setEditImage(item.reference_image||'');setEditSuggestionText(item.suggestion_text||'');setEditSuggestion(!!item.suggestion_text);setEditItemType(item.item_type||"check");setEditFuelSide(item.fuel_side||'');setEditPhase(item.phase||'prevuelo');}} className="p-1.5 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-500"><Edit2 className="w-3.5 h-3.5"/></button>
                    <button onClick={()=>handleDeleteClick(item.id)} className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500"><Trash2 className="w-3.5 h-3.5"/></button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      {adding ? (
        <div className={`p-4 rounded-2xl border-2 border-dashed space-y-3 ${darkMode?'border-gray-600 bg-gray-700/30':'border-gray-300 bg-gray-50'}`}>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={`block text-xs font-medium mb-1 ${darkMode?'text-gray-400':'text-gray-600'}`}>Sección</label>
              <input list="sections-list" value={newItem.section_name} onChange={e=>setNewItem(n=>({...n,section_name:e.target.value}))} className={`${inputClass} w-full`} placeholder="Ej: Sección Fluidos" />
              <datalist id="sections-list">{sections.map(s=><option key={s} value={s}/>)}</datalist>
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${darkMode?'text-gray-400':'text-gray-600'}`}>Orden sección</label>
              <input type="number" value={newItem.section_order} min={0} onChange={e=>setNewItem(n=>({...n,section_order:parseInt(e.target.value)||0}))} className={`${inputClass} w-full`} />
            </div>
          </div>
          <div>
            <label className={`block text-xs font-medium mb-1 ${darkMode?'text-gray-400':'text-gray-600'}`}>Texto del item *</label>
            <input value={newItem.item_text} onChange={e=>setNewItem(n=>({...n,item_text:e.target.value}))} className={`${inputClass} w-full`} placeholder="Ej: Verificar nivel de aceite" />
          </div>
          <div>
            <label className={`block text-xs font-medium mb-1 ${darkMode?'text-gray-400':'text-gray-600'}`}>Tipo de ítem</label>
            <select value={newItem.item_type} onChange={e=>setNewItem(n=>({...n,item_type:e.target.value, fuel_side: e.target.value === 'fuel' ? n.fuel_side : ''}))} className={`${inputClass} w-full`}>
              <option value="check">✅ Ítem normal (check)</option>
              <option value="hobbs">🕐 Horómetro</option>
              <option value="oil">🛢 Aceite</option>
              <option value="fuel">⛽ Combustible</option>
            </select>
          </div>
          <div>
            <label className={`block text-xs font-medium mb-1 ${darkMode?'text-gray-400':'text-gray-600'}`}>Fase</label>
            <select value={newItem.phase} onChange={e=>setNewItem(n=>({...n,phase:e.target.value}))} className={`${inputClass} w-full`}>
              {PHASE_OPTIONS.map(p=><option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          {newItem.item_type==='fuel' && (
            <div>
              <label className={`block text-xs font-medium mb-1 ${darkMode?'text-gray-400':'text-gray-600'}`}>Lado del tanque *</label>
              <select value={newItem.fuel_side} onChange={e=>setNewItem(n=>({...n,fuel_side:e.target.value}))} className={`${inputClass} w-full`}>
                <option value="">— Seleccioná un lado —</option>
                <option value="left">⬅ Izquierdo</option>
                <option value="right">➡ Derecho</option>
                <option value="both">⬅➡ Ambos (1 ítem, 2 inputs)</option>
              </select>
              <p className={`mt-1 text-xs ${darkMode?'text-gray-500':'text-gray-500'}`} style={{ fontStyle:'italic' }}>
                Aeronaves con un solo tanque ignoran este valor y muestran un único input.
              </p>
            </div>
          )}
          <div>
            <label className={`block text-xs font-medium mb-1 ${darkMode?'text-gray-400':'text-gray-600'}`}>Imagen de referencia <span className={darkMode?'text-gray-500':'text-gray-400'}>(opcional)</span></label>
            <div className="flex gap-2 items-center">
              <label className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold cursor-pointer transition-all ${uploading?'opacity-50 pointer-events-none':''} ${darkMode?'bg-gray-600 text-white hover:bg-gray-500':'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                {uploading?<Loader className="w-4 h-4 animate-spin"/>:<Upload className="w-4 h-4"/>}
                {uploading?'Subiendo...':'Subir imagen'}
                <input type="file" accept="image/*" className="hidden" onChange={e=>{const f=e.target.files[0];if(f)handleImageUpload(f,(url)=>setNewItem(n=>({...n,reference_image:url})),newItem.section_name,newItem.item_text);e.target.value='';}} />
              </label>
              {newItem.reference_image && (<><img src={newItem.reference_image} alt="preview" style={{ width:'36px',height:'36px',borderRadius:'8px',objectFit:'cover',flexShrink:0 }} onError={e=>e.target.style.display='none'} /><button onClick={()=>setNewItem(n=>({...n,reference_image:''}))} className="text-red-400 hover:text-red-500"><X className="w-4 h-4"/></button></>)}
            </div>
          </div>
          <div>
            <label className={`block text-xs font-medium mb-1 ${darkMode?'text-gray-400':'text-gray-600'}`}>💡 Sugerencia para el piloto <span className={darkMode?'text-gray-500':'text-gray-400'}>(opcional)</span></label>
            <textarea value={newItem.suggestion_text} onChange={e=>setNewItem(n=>({...n,suggestion_text:e.target.value,has_suggestion:!!e.target.value}))} className={`${inputClass} w-full`} rows={2} placeholder="Ej: Este avión consume 30L/hora..." />
          </div>
          <div className="flex gap-2">
            <button onClick={handleAddItem} className="flex-1 py-2 bg-blue-500 text-white rounded-xl font-semibold text-sm hover:bg-blue-600 flex items-center justify-center gap-1"><Check className="w-4 h-4"/>Agregar</button>
            <button onClick={()=>setAdding(false)} className={`px-4 py-2 rounded-xl font-semibold text-sm ${darkMode?'bg-gray-600 text-white hover:bg-gray-500':'bg-gray-200 hover:bg-gray-300'}`}>Cancelar</button>
          </div>
        </div>
      ) : (
        <button onClick={()=>setAdding(true)} className={`w-full py-2.5 rounded-2xl border-2 border-dashed flex items-center justify-center gap-2 text-sm font-medium transition-all ${darkMode?'border-gray-600 text-gray-400 hover:bg-gray-700':'border-gray-300 text-gray-500 hover:bg-gray-50'}`}>
          <Plus className="w-4 h-4"/>Agregar item al checklist
        </button>
      )}
      <GeneralConfirmModal
        isOpen={!!deleteTargetId}
        title={t('confirm_modal_checklist_item_delete_title')}
        message={t('confirm_modal_checklist_item_delete_message')}
        confirmLabel={t('confirm_modal_default_confirm_delete')}
        variant="danger"
        isLoading={deletingItem}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTargetId(null)}
        darkMode={darkMode}
      />
    </div>
  );
};

// ─── Aircraft Modal ───────────────────────────────────────────────
const AircraftModal = ({ aircraft, onSave, onClose, darkMode }) => {
  const { t } = useTranslation('admin');
  const isEdit = !!aircraft?.id;
  const [form, setForm] = useState({
    manufacturer:aircraft?.manufacturer||'', model_name:aircraft?.model_name||'',
    registration:aircraft?.registration||'', year:aircraft?.year||'',
    max_weight_kg:aircraft?.max_weight_kg||'', fuel_capacity_liters:aircraft?.fuel_capacity_liters||'',
    oil_capacity_quarts:aircraft?.oil_capacity_quarts||'', notes:aircraft?.notes||'',
    next_maintenance_date:aircraft?.next_maintenance_date?aircraft.next_maintenance_date.split('T')[0]:'',
    airworthiness_expiry:aircraft?.airworthiness_expiry?aircraft.airworthiness_expiry.split('T')[0]:'',
    last_maintenance_date:aircraft?.last_maintenance_date?aircraft.last_maintenance_date.split('T')[0]:'',
    service_interval_hours:aircraft?.service_interval_hours||100,
    fuel_capacity_left:aircraft?.fuel_capacity_left||'',
    fuel_capacity_right:aircraft?.fuel_capacity_right||'',
    fuel_unit:aircraft?.fuel_unit||'',
    single_tank:aircraft?.single_tank===true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const inputClass = `w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`;
  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try { await onSave(isEdit ? aircraft.id : null, form, isEdit); onClose(); }
    catch (err) { setError(err.response?.data?.message || 'Error al guardar'); }
    finally { setLoading(false); }
  };
  return (
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <motion.div initial={{ scale:0.9, y:20 }} animate={{ scale:1, y:0 }} exit={{ scale:0.9, y:20 }}
        className={`w-full max-w-lg rounded-3xl shadow-2xl p-6 my-8 ${darkMode ? 'bg-gray-800' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{isEdit ? t('aircraft_edit_title') : t('aircraft_new_title')}</h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"><X className="w-5 h-5"/></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={`block text-sm font-medium mb-1 ${darkMode?'text-gray-300':'text-gray-700'}`}>Fabricante *</label><input type="text" required value={form.manufacturer} onChange={e=>setForm(f=>({...f,manufacturer:e.target.value}))} className={inputClass} placeholder="Cessna"/></div>
            <div><label className={`block text-sm font-medium mb-1 ${darkMode?'text-gray-300':'text-gray-700'}`}>Modelo *</label><input type="text" required value={form.model_name} onChange={e=>setForm(f=>({...f,model_name:e.target.value}))} className={inputClass} placeholder="172 Skyhawk"/></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={`block text-sm font-medium mb-1 ${darkMode?'text-gray-300':'text-gray-700'}`}>Matrícula</label><input type="text" value={form.registration} onChange={e=>setForm(f=>({...f,registration:e.target.value}))} className={inputClass} placeholder="LV-ABC"/></div>
            <div><label className={`block text-sm font-medium mb-1 ${darkMode?'text-gray-300':'text-gray-700'}`}>Año</label><input type="number" value={form.year} onChange={e=>setForm(f=>({...f,year:e.target.value}))} className={inputClass} placeholder="1985" min="1900" max="2030"/></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className={`block text-sm font-medium mb-1 ${darkMode?'text-gray-300':'text-gray-700'}`}>Peso máx (kg)</label><input type="number" value={form.max_weight_kg} onChange={e=>setForm(f=>({...f,max_weight_kg:e.target.value}))} className={inputClass} placeholder="1157"/></div>
            <div><label className={`block text-sm font-medium mb-1 ${darkMode?'text-gray-300':'text-gray-700'}`}>Combustible (L)</label><input type="number" value={form.fuel_capacity_liters} onChange={e=>setForm(f=>({...f,fuel_capacity_liters:e.target.value}))} className={inputClass} placeholder="180"/></div>
            <div><label className={`block text-sm font-medium mb-1 ${darkMode?'text-gray-300':'text-gray-700'}`}>Aceite (qt)</label><input type="number" value={form.oil_capacity_quarts} onChange={e=>setForm(f=>({...f,oil_capacity_quarts:e.target.value}))} className={inputClass} placeholder="8"/></div>
          </div>
          <div className={`p-4 rounded-2xl ${darkMode?'bg-gray-700/50 border border-gray-600':'bg-blue-50 border border-blue-100'}`}>
            <div className="flex items-center gap-2 mb-3">
              <Fuel className={`w-4 h-4 ${darkMode?'text-blue-400':'text-blue-600'}`}/>
              <p className={`text-sm font-bold ${darkMode?'text-blue-400':'text-blue-700'}`}>Tracking de combustible por tanque</p>
              <p className={`text-xs ${darkMode?'text-gray-500':'text-gray-400'}`}>(opcional)</p>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className={`block text-xs font-semibold mb-1 ${darkMode?'text-gray-300':'text-gray-600'}`}>Unidad operativa</label>
                <select value={form.fuel_unit} onChange={e=>setForm(f=>({...f,fuel_unit:e.target.value}))} className={inputClass}>
                  <option value="">— Default org —</option>
                  <option value="L">Litros (L)</option>
                  <option value="GAL">Galones imp. (GAL)</option>
                  <option value="US-GAL">Galones US (US-GAL)</option>
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer select-none pb-2">
                  <input type="checkbox" checked={form.single_tank} onChange={e=>setForm(f=>({...f,single_tank:e.target.checked}))} style={{ width:16,height:16,cursor:'pointer' }} />
                  <span className={`text-xs font-semibold ${darkMode?'text-gray-300':'text-gray-700'}`}>Aeronave de un solo tanque</span>
                </label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={`block text-xs font-semibold mb-1 ${darkMode?'text-gray-300':'text-gray-600'}`}>Capacidad tanque izq.</label>
                <input type="number" step="0.1" min="0" value={form.fuel_capacity_left} onChange={e=>setForm(f=>({...f,fuel_capacity_left:e.target.value}))} className={inputClass} placeholder="90"/>
              </div>
              <div>
                <label className={`block text-xs font-semibold mb-1 ${darkMode?'text-gray-300':'text-gray-600'}`}>Capacidad tanque der.</label>
                <input type="number" step="0.1" min="0" disabled={form.single_tank} value={form.single_tank ? '' : form.fuel_capacity_right} onChange={e=>setForm(f=>({...f,fuel_capacity_right:e.target.value}))} className={`${inputClass} ${form.single_tank?'opacity-40 cursor-not-allowed':''}`} placeholder={form.single_tank?'—':'90'}/>
              </div>
            </div>
          </div>
          <div className={`p-4 rounded-2xl ${darkMode?'bg-gray-700/50 border border-gray-600':'bg-amber-50 border border-amber-100'}`}>
            <div className="flex items-center gap-2 mb-3">
              <Wrench className={`w-4 h-4 ${darkMode?'text-amber-400':'text-amber-600'}`}/>
              <p className={`text-sm font-bold ${darkMode?'text-amber-400':'text-amber-700'}`}>Mantenimiento y aeronavegabilidad</p>
              <p className={`text-xs ${darkMode?'text-gray-500':'text-gray-400'}`}>(opcional)</p>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div><label className={`block text-xs font-semibold mb-1 ${darkMode?'text-gray-300':'text-gray-600'}`}>Intervalo de service (hs de vuelo)</label><input type="number" value={form.service_interval_hours} onChange={e=>setForm(f=>({...f,service_interval_hours:e.target.value}))} className={inputClass} placeholder="100" min="1"/></div>
              <div><label className={`block text-xs font-semibold mb-1 ${darkMode?'text-gray-300':'text-gray-600'}`}>Próximo mantenimiento</label><input type="date" value={form.next_maintenance_date} onChange={e=>setForm(f=>({...f,next_maintenance_date:e.target.value}))} className={inputClass}/></div>
              <div><label className={`block text-xs font-semibold mb-1 ${darkMode?'text-gray-300':'text-gray-600'}`}>Último mantenimiento</label><input type="date" value={form.last_maintenance_date} onChange={e=>setForm(f=>({...f,last_maintenance_date:e.target.value}))} className={inputClass}/></div>
              <div><label className={`block text-xs font-semibold mb-1 ${darkMode?'text-gray-300':'text-gray-600'}`}>Vencimiento aeronavegabilidad</label><input type="date" value={form.airworthiness_expiry} onChange={e=>setForm(f=>({...f,airworthiness_expiry:e.target.value}))} className={inputClass}/></div>
            </div>
          </div>
          <div><label className={`block text-sm font-medium mb-1 ${darkMode?'text-gray-300':'text-gray-700'}`}>Notas</label><textarea value={form.notes} rows={2} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} className={inputClass} placeholder="Observaciones adicionales..."/></div>
          {error && <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"><AlertCircle className="w-4 h-4 flex-shrink-0"/>{error}</div>}
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={loading} className="flex-1 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold flex items-center justify-center gap-2">
              {loading?<Loader className="w-4 h-4 animate-spin"/>:<Check className="w-4 h-4"/>}
              {isEdit ? t('aircraft_save_changes') : t('aircraft_create')}
            </button>
            <button type="button" onClick={onClose} className={`px-4 py-3 rounded-xl font-bold transition-all ${darkMode?'bg-gray-700 hover:bg-gray-600 text-white':'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
              {t('cancel', { ns: 'common' })}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// ─── Main Component ───────────────────────────────────────────────
const AdminAircraft = ({ darkMode, orgFeatures, userRole, onNavigateTo }) => {
  const { t } = useTranslation('admin');
  const [aircraft, setAircraft]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [showModal, setShowModal]       = useState(false);
  const [editAircraft, setEditAircraft] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [expandedId, setExpandedId]     = useState(null);
  const [expandedTab, setExpandedTab]   = useState('checklist');
  const expandedRef = React.useRef({});
  const [error, setError]               = useState('');
  const [qrTarget, setQrTarget]         = useState(null);
  const [toggleTarget, setToggleTarget] = useState(null);
  const [toggleLoading, setToggleLoading] = useState(false);
  // Resumen del sistema NUEVO de componentes por aeronave (píldora del listado).
  // Shape: { [aircraftId]: { remaining, threshold, status } } — status: 'overdue' | 'near' | 'ok'.
  const [componentSummary, setComponentSummary] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await aircraftService.getAll();
      const payload = res.data?.data ?? res.data ?? [];
      setAircraft(Array.isArray(payload) ? payload : []);
    } catch { setError('Error al cargar aeronaves'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Carga en paralelo los componentes de cada aeronave y arma un resumen
  // (el componente más próximo a vencer) para alimentar la píldora del listado
  // con el sistema NUEVO en lugar del cálculo legacy hobbs_*.
  useEffect(() => {
    let cancelled = false;
    const list = Array.isArray(aircraft) ? aircraft : [];
    if (list.length === 0) { setComponentSummary({}); return; }
    (async () => {
      const entries = await Promise.all(list.map(async (ac) => {
        try {
          const r = await serviceComponentsService.listByAircraft(ac.id);
          const comps = Array.isArray(r?.data?.data) ? r.data.data : [];
          if (comps.length === 0) return [ac.id, null];
          let pick = null;
          for (const c of comps) {
            const rem = parseFloat(c.hours_remaining);
            if (!Number.isFinite(rem)) continue;
            if (pick == null || rem < pick.rem) {
              pick = { rem, threshold: parseFloat(c.alert_threshold), status: c.status };
            }
          }
          if (!pick) return [ac.id, null];
          const threshold = Number.isFinite(pick.threshold) ? pick.threshold : 10;
          const status = pick.status || (pick.rem <= 0 ? 'overdue' : (pick.rem <= threshold ? 'near' : 'ok'));
          return [ac.id, { remaining: pick.rem, threshold, status }];
        } catch { return [ac.id, null]; }
      }));
      if (cancelled) return;
      const map = {};
      for (const [id, val] of entries) if (val) map[id] = val;
      setComponentSummary(map);
    })();
    return () => { cancelled = true; };
  }, [aircraft]);

  const handleSave = async (id, form, isEdit) => {
    if (isEdit) await aircraftService.update(id, form);
    else await aircraftService.create(form);
    await load();
  };
  const handleToggle = (ac) => setToggleTarget(ac);
  const confirmToggle = async () => {
    if (!toggleTarget) return;
    setToggleLoading(true);
    try { await aircraftService.update(toggleTarget.id, { is_active: !toggleTarget.is_active }); await load(); setToggleTarget(null); }
    catch { setError('Error al actualizar'); } finally { setToggleLoading(false); }
  };
  const handleDelete = async (id, permanent) => {
    try { await aircraftService.delete(id + (permanent ? '?permanent=true' : '')); await load(); }
    catch (err) { setError(err.response?.data?.message || 'Error al eliminar'); throw err; }
  };

  const filtered = (aircraft || []).filter(a =>
    `${a.manufacturer} ${a.model_name} ${a.registration || ''}`.toLowerCase().includes(search.toLowerCase())
  );
  const cardClass = `rounded-2xl border transition-all ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{t('aircraft_title')}</h2>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {(aircraft||[]).filter(a=>a.is_active).length} {t('aircraft_activate').toLowerCase()} · {(aircraft||[]).length} total
          </p>
        </div>
        <motion.button onClick={() => { setEditAircraft(null); setShowModal(true); }}
          className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl font-bold shadow-lg hover:shadow-xl transition-all"
          whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
          <Plus className="w-5 h-5"/> {t('aircraft_new')}
        </motion.button>
      </div>

      <div className={`${cardClass} p-3`}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
          <input type="text" placeholder={t('aircraft_search')} value={search} onChange={e => setSearch(e.target.value)}
            className={`w-full pl-10 pr-4 py-2.5 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode?'bg-gray-700 border-gray-600 text-white':'bg-gray-50 border-gray-200 text-gray-900'}`}/>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-2xl text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0"/><span>{error}</span>
          <button onClick={()=>setError('')} className="ml-auto"><X className="w-4 h-4"/></button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader className="w-8 h-8 animate-spin text-blue-500"/></div>
      ) : filtered.length === 0 ? (
        <div className={`${cardClass} text-center py-16`}>
          <Plane className="w-12 h-12 mx-auto mb-3 text-gray-400"/>
          <p className={`font-medium ${darkMode?'text-gray-400':'text-gray-600'}`}>{t('aircraft_empty')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((ac, i) => (
            <motion.div key={ac.id} initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} transition={{ delay:i*0.04 }}
              className={`${cardClass} overflow-hidden`}>
              <div style={{ padding:'14px 16px' }}>
                <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${ac.is_active?'bg-blue-100':'bg-gray-100'}`}>
                    <Plane className={`w-5 h-5 ${ac.is_active?'text-blue-600':'text-gray-400'}`}/>
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                      <p className={`font-bold text-sm ${darkMode?'text-white':'text-gray-900'} ${!ac.is_active?'opacity-50':''}`}>{ac.manufacturer} {ac.model_name}</p>
                      {ac.registration && <span className={`text-xs px-2 py-0.5 rounded-full font-mono font-bold ${darkMode?'bg-gray-700 text-gray-300':'bg-gray-100 text-gray-600'}`}>{ac.registration}</span>}
                      <AogBadge aircraft={ac} size="sm" />
                      {!ac.is_active && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full border">{t('aircraft_inactive_badge')}</span>}
                    </div>
                    {(() => {
                      // Píldora basada en el sistema NUEVO de componentes:
                      // muestra el componente activo más próximo a vencer.
                      const s = componentSummary[ac.id];
                      if (!s) return null;
                      const rem = Math.round(s.remaining * 10) / 10;
                      if (s.status === 'overdue') return <div style={{ marginTop:4 }}><span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold border border-red-200">⚠️ {t('service_overdue', { ns:'common' })}</span></div>;
                      if (s.status === 'near')    return <div style={{ marginTop:4 }}><span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold border border-amber-200">⚠️ Service en {rem}hs</span></div>;
                      return <div style={{ marginTop:4 }}><span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold border border-green-200">✅ {rem}hs {t('service_remaining', { ns:'common', hours:'' }).replace('hs','').trim()}</span></div>;
                    })()}
                    <div className={`flex gap-3 text-xs mt-1 flex-wrap ${darkMode?'text-gray-400':'text-gray-500'}`}>
                      {ac.fuel_capacity_liters && <span>⛽ {ac.fuel_capacity_liters}L</span>}
                      {ac.oil_capacity_quarts  && <span>🛢️ {ac.oil_capacity_quarts}qt</span>}
                      {ac.hobbs_current > 0    && <span>🕐 {ac.hobbs_current}hs</span>}
                      {ac.inspection_count > 0 && <span>📋 {ac.inspection_count} insp.</span>}
                      {ac.next_maintenance_date && (
                        <span className={(() => { const d=Math.round((new Date(ac.next_maintenance_date)-new Date())/86400000); return d<=7?'text-red-500 font-bold':d<=30?'text-amber-500 font-semibold':''; })()}>
                          🔧 {new Date(ac.next_maintenance_date).toLocaleDateString('es-AR')}
                        </span>
                      )}
                    </div>
                  </div>
                  <button onClick={()=>{setExpandedId(expandedId===ac.id?null:ac.id);setExpandedTab('checklist');}}
                    className={`p-2 rounded-xl transition-all flex-shrink-0 ${darkMode?'hover:bg-gray-700 text-gray-400':'hover:bg-gray-100 text-gray-500'}`}>
                    {expandedId===ac.id?<ChevronUp className="w-5 h-5"/>:<ChevronDown className="w-5 h-5"/>}
                  </button>
                </div>
                <div style={{ display:'flex', gap:6, marginTop:10, paddingTop:10, borderTop:darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid #f1f5f9' }}>
                  <button onClick={()=>{setExpandedId(expandedId===ac.id?null:ac.id);setExpandedTab('checklist');}}
                    style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:4, padding:'7px', borderRadius:10, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:11, fontWeight:600, background:darkMode?'rgba(255,255,255,0.05)':'#f8fafc', color:darkMode?'rgba(255,255,255,0.5)':'#64748b' }}>
                    <List style={{ width:13, height:13 }}/> {t('aircraft_tab_checklist')}
                  </button>
                  {orgFeatures?.qr_access !== false && (
                    <button onClick={()=>setQrTarget({ id:ac.id, name:`${ac.manufacturer} ${ac.model_name}`, registration:ac.registration, manufacturer:ac.manufacturer })}
                      style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:4, padding:'7px', borderRadius:10, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:11, fontWeight:600, background:darkMode?'rgba(255,255,255,0.05)':'#f8fafc', color:darkMode?'rgba(255,255,255,0.5)':'#64748b' }}>
                      <QrCode style={{ width:13, height:13 }}/> {t('aircraft_tab_checklist', {defaultValue:'QR'})}
                    </button>
                  )}
                  <button onClick={()=>{setEditAircraft(ac);setShowModal(true);}}
                    style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:4, padding:'7px', borderRadius:10, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:11, fontWeight:600, background:darkMode?'rgba(37,99,235,0.1)':'rgba(37,99,235,0.06)', color:'#2563eb' }}>
                    <Edit2 style={{ width:13, height:13 }}/> {t('aircraft_edit')}
                  </button>
                  <button onClick={()=>handleToggle(ac)}
                    style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:4, padding:'7px', borderRadius:10, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:11, fontWeight:600, background:ac.is_active?(darkMode?'rgba(245,158,11,0.1)':'rgba(245,158,11,0.06)'):(darkMode?'rgba(22,163,74,0.1)':'rgba(22,163,74,0.06)'), color:ac.is_active?'#d97706':'#16a34a' }}>
                    {ac.is_active?<ToggleRight style={{ width:13, height:13 }}/>:<ToggleLeft style={{ width:13, height:13 }}/>}
                    {ac.is_active?t('aircraft_deactivate'):t('aircraft_activate')}
                  </button>
                  <button onClick={()=>setDeleteTarget(ac)}
                    style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'7px 10px', borderRadius:10, border:'none', cursor:'pointer', background:darkMode?'rgba(239,68,68,0.1)':'rgba(239,68,68,0.06)', color:'#ef4444' }}>
                    <Trash2 style={{ width:13, height:13 }}/>
                  </button>
                </div>
              </div>
              <AnimatePresence>
                {expandedId===ac.id && (
                  <motion.div initial={{ height:0, opacity:0 }} animate={{ height:'auto', opacity:1 }}
                    exit={{ height:0, opacity:0 }} transition={{ duration:0.3 }}
                    onAnimationComplete={() => { if (expandedRef.current[ac.id]) expandedRef.current[ac.id].scrollIntoView({ behavior:'smooth', block:'nearest' }); }}
                    ref={el => expandedRef.current[ac.id]=el}
                    style={{ maxHeight:'70vh', overflowY:'auto' }}
                    className={`px-4 pb-4 border-t ${darkMode?'border-gray-700':'border-gray-100'}`}>
                    <div className="flex gap-2 mt-3 mb-1 flex-wrap">
                      <button onClick={()=>setExpandedTab('checklist')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${expandedTab==='checklist'?'bg-blue-500 text-white':darkMode?'bg-gray-700 text-gray-400':'bg-gray-100 text-gray-500'}`}>
                        📋 {t('aircraft_tab_checklist')}
                      </button>
                      {orgFeatures?.pre_landing && (
                        <button onClick={()=>setExpandedTab('prelanding')}
                          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${expandedTab==='prelanding'?'bg-cyan-500 text-white':darkMode?'bg-gray-700 text-gray-400':'bg-gray-100 text-gray-500'}`}>
                          🛬 {t('aircraft_tab_prelanding')}
                        </button>
                      )}
                      <button onClick={()=>setExpandedTab('hobbs')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${expandedTab==='hobbs'?'bg-amber-500 text-white':darkMode?'bg-gray-700 text-gray-400':'bg-gray-100 text-gray-500'}`}>
                        🕐 {t('aircraft_tab_hobbs')}
                      </button>
                      <button onClick={()=>setExpandedTab('audio')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${expandedTab==='audio'?'bg-purple-500 text-white':darkMode?'bg-gray-700 text-gray-400':'bg-gray-100 text-gray-500'}`}>
                        🎙️ {t('aircraft_tab_audio')}
                      </button>
                      <button onClick={()=>setExpandedTab('equipment')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${expandedTab==='equipment'?'bg-emerald-500 text-white':darkMode?'bg-gray-700 text-gray-400':'bg-gray-100 text-gray-500'}`}>
                        ⚖️ {t('aircraft_tab_equipment')}
                      </button>
                    </div>
                    {expandedTab==='checklist'  && <ChecklistSection aircraftId={ac.id} darkMode={darkMode}/>}
                    {expandedTab==='prelanding' && orgFeatures?.pre_landing && <PreLandingSection aircraftId={ac.id} darkMode={darkMode}/>}
                    {expandedTab==='audio'      && <AudioOfflineSection aircraftId={ac.id} darkMode={darkMode}/>}
                    {expandedTab==='hobbs'      && <AircraftServiceComponentsWidget aircraftId={ac.id} aircraft={ac} userRole={userRole} darkMode={darkMode} onNavigateToMaintenance={() => onNavigateTo?.('maintenance')}/>}
                    {expandedTab==='equipment'  && <EquipmentTab aircraft={ac} darkMode={darkMode}/>}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {showModal && <AircraftModal aircraft={editAircraft} onSave={handleSave} onClose={()=>{ setShowModal(false); setEditAircraft(null); }} darkMode={darkMode}/>}
        {deleteTarget && <DeleteModal aircraft={deleteTarget} onConfirm={handleDelete} onClose={()=>setDeleteTarget(null)} darkMode={darkMode}/>}
        {qrTarget && <QRModal aircraft={qrTarget} darkMode={darkMode} onClose={()=>setQrTarget(null)}/>}
        {toggleTarget && <GeneralConfirmModal
          title={`${toggleTarget.is_active ? t('aircraft_deactivate') : t('aircraft_activate')} aeronave`}
          message={`¿${toggleTarget.is_active ? t('aircraft_deactivate') : t('aircraft_activate')} ${toggleTarget.manufacturer} ${toggleTarget.model_name}${toggleTarget.registration?` (${toggleTarget.registration})`:''}`}
          confirmLabel={toggleTarget.is_active ? t('aircraft_deactivate') : t('aircraft_activate')}
          confirmColor={toggleTarget.is_active?'#ef4444':'#22c55e'}
          icon={toggleTarget.is_active?ToggleLeft:ToggleRight}
          iconBg={toggleTarget.is_active?'rgba(239,68,68,0.1)':'rgba(34,197,94,0.1)'}
          onConfirm={confirmToggle}
          onCancel={()=>setToggleTarget(null)}
          darkMode={darkMode}
          isLoading={toggleLoading}
        />}
      </AnimatePresence>
      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
};

export default AdminAircraft;
